//
// Angular
//
import { Injectable } from '@angular/core';
import {webSocket, WebSocketSubject} from 'rxjs/webSocket';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { ViewConstants } from '../../constants';
import { Subject, BehaviorSubject, Observable, of } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';
import { SocketPayload } from 'src/app/shared/model/api/SocketPayload';
import * as fromCore from '../state'
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import { getUserInfo } from '../state/selectors';
import { isNil, uniq } from 'lodash';
import { SharedDataService } from './shared-data.service';
import * as appStatusActions from '../../core/state/actions/app-status.action'
@Injectable(
    {
        providedIn: 'root'
    }
)
export class WebSocketManagerService {
    private heartbeat_interval = null;
    private missed_heartbeats = 0;
    private ws;
    private dealTransactionId: string = null;
    private salesPerson: string;
    private concurrentDealViewers:string[] = [];
    public concurrentDealViewersSubject = new BehaviorSubject<string[]>([]);
    //
    // try reconnecting in millisecond
    //
    private readonly reconnectDelay: 5000;
    private readonly data: Subject<SocketMessage>;
    private readonly sendToFinanceEvent: Subject<any>;
    //
    // get the authentication token 
    //
    private get token() {
        // return sessionStorage.getItem(ViewConstants.ID_TOKEN_HTTP_CLIENT);
        return sessionStorage.getItem(ViewConstants.ACCESS_TOKEN_HTTP_CLIENT);
    }
    //
    // indicates if socket is connected or not
    //
    private socketIsOpen = false;
    public _subs = new SubSink();

    constructor(private readonly _store: Store<fromCore.AppState>,private readonly environment: EnvironmentConfiguration, private readonly sharedDataService: SharedDataService){
        this.data = new Subject<SocketMessage>();
        this.sendToFinanceEvent = new Subject<any>();
        this.sharedDataService.dealTransactionId.subscribe(id => this.dealTransactionId = id);
    }

    //
    // unique connectionId for this socket connection
    //
    private connectionId: string;
    //
    // Connection ID
    //
    get ConnectionID() {
        return this.connectionId;
    }

    /** 
    * listens to incoming messages
    * @description usage: returns true if the payload has the givenType flag set to 1. you can not simply use equal because its a flag enum. use this method instead
    * @param payload: the payload you want to check
    * @param givenType: the flag you want to see its set or not
    */
    public hasType(payload: SocketPayloadType, givenType: SocketPayloadType) {
        return (payload & givenType) == givenType;
    }
    /** 
     * listens to incoming messages
     * @description usage: pass SocketPayloadType you want to listen to. you can pass betwise ORed status if you want to listen to a message that has ALL of those types
     * @param SocketPayloadType
     */
    listen(type) {
        this.checkConnection();
        //return this.data.asObservable().pipe(filter(e => (e.type & type) === type), map(e => e.payload));
        return this.data.asObservable().pipe(filter(e => (e.type & type) === type));
    }
    /** 
    * listens to incoming messages
    * @description usage: pass SocketPayloadType you want to listen to. you can pass betwise ORed status if you want to listen to a message that has EITHER of those types
    * @param SocketPayloadType
    */
    listenToEach(type) {
        this.checkConnection();
        return this.data.asObservable().pipe(filter(e => <any>(e.type & type) >= 1));
    }
    //
    // send new message
    //
    send(msg) {
        this.checkConnection();
        if (this.socketIsOpen) {
            this.ws.next(msg);
        }
        else {
            clearInterval(this.heartbeat_interval);
            this.heartbeat_interval = null;
            setTimeout(() => {
                this.send(msg);
            }, this.reconnectDelay);
        }
    }

    //
    // establish the socket connection
    //
    public triggerDealLeftEvent(){
        this.send({message:"sendmessage",action:"sendmessage", data:{type:"DEAL_LEFT", deal:{dealTransactionId:this.dealTransactionId,salesPerson: this.salesPerson}}})
    }

    public triggerDealWSEvent(message: string, action: string, type: string, dealTransactionId: string) {
        this.send({message: message, action: action, data:{type: type, deal: {dealTransactionId: dealTransactionId, salesPerson: this.salesPerson}}})
    }

    public getConcurrentDealViewers(payload: SocketMessage){
        switch (payload.type) {
            case SocketPayloadType.ActiveConnections:
                const concurrentDealViewers = payload.payload.data[this.dealTransactionId];
                this.concurrentDealViewers = [];
                if(!isNil(concurrentDealViewers) && concurrentDealViewers.length > 0){
                    this.concurrentDealViewers = concurrentDealViewers
                    .map(viewer => viewer.salesPerson).filter(salesPerson => salesPerson?.toLowerCase() !== this.salesPerson.toLowerCase());
                }
                break;
            case SocketPayloadType.DealView:
                if((payload?.payload?.deal?.dealTransactionId === this.dealTransactionId) && (payload?.payload?.deal?.salesPerson?.toLowerCase() !== this.salesPerson.toLowerCase())) 
                    {this.concurrentDealViewers.push(payload.payload.deal.salesPerson);}
                break;
            case SocketPayloadType.DealLeft:
                if(payload?.payload?.deal?.dealTransactionId === this.dealTransactionId) 
                {this.concurrentDealViewers = this.concurrentDealViewers.filter(viewer => viewer !== payload.payload.deal.salesPerson);}
                break;
            case SocketPayloadType.UserLogout:
                if(payload?.payload?.deal?.dealTransactionId === this.dealTransactionId) 
                {this.concurrentDealViewers = this.concurrentDealViewers.filter(viewer => viewer !== payload.payload.deal.salesPerson);}
                break;
            case SocketPayloadType.Dashboard:
                if((payload?.payload?.deal?.dealTransactionId === this.dealTransactionId) && (payload?.payload?.deal?.salesPerson?.toLowerCase() !== this.salesPerson.toLowerCase())) 
                {this.concurrentDealViewers.push(payload.payload.deal.salesPerson);}
                break;
                 case SocketPayloadType.CreditAppSubmitted:
                if((payload?.payload?.deal?.dealTransactionId === this.dealTransactionId) && (payload?.payload?.deal?.salesPerson?.toLowerCase() !== this.salesPerson.toLowerCase())) 
                {this.concurrentDealViewers.push(payload.payload.deal.salesPerson);}
                break;
            default:
                // this.concurrentDealViewers = [];
                break;
        }
        this.concurrentDealViewersSubject.next(uniq(this.concurrentDealViewers.filter(viewer => !isNil(viewer))));
    }
    listenSendToFinanceEvent(){
        return this.sendToFinanceEvent.asObservable().pipe(take(1));
    }
    public connect() {
        //
        // delay connection until a token id is available
        //
        if (!this.token) {
            clearInterval(this.heartbeat_interval);
            this.heartbeat_interval = null;
            setTimeout(() => {
                this.connect();
            }, this.reconnectDelay);
            return;
        }


        if (!this.socketIsOpen && this.sharedDataService.isUserAuthorized) {
            // let socketEndPoint= this.environment.SOCKET_ENDPOINT + window.btoa(deflate("Bearer "+this.token,{ to: 'string' }));
            let socketEndPoint = `${this.environment.SOCKET_ENDPOINT  }Bearer ${  this.token}`;

            // Adding socket Number from the User Info service to the query param of all websocket calls
            this._subs.sink = this._store.select(getUserInfo).subscribe(userInfo => {
                this.salesPerson = `${userInfo.first_name} ${userInfo.last_name}`
                if (userInfo?.hasOwnProperty('socketNumber')) {
                    socketEndPoint = `${socketEndPoint  }&socketNumber=${  userInfo.socketNumber}`;
                    this.ws = webSocket(socketEndPoint);

                    //get the socket connection id.

                    this.ws.asObservable().subscribe(
                        (msg: SocketPayload) => {
                            this.checkError(true);
                            const payload: SocketMessage = {
                                type: this.getType(msg),
                                payload: msg
                            }
                            if (payload.type == SocketPayloadType.ConnectionId) {
                                this.heartbeatHandler();
                                this.connectionId = payload.payload.connectionId;
                                this.sharedDataService.webSocketConnectionID = this.connectionId;
                            }
                            else {
                                this.getConcurrentDealViewers(payload)

                                //
                                //  Socket messages will only be kept if:
                                //  1. coming from a different connectionId than this login's connectionId
                                //  2. if there is a connectionId and the type is dealview or dealUpdate
                                //  if Type is unknown should be ignored also
                                //  rest will be ignored and wont be added to the queue 
                                //
                                const hasConnectionId = !isNil(payload.payload.connectionId);
                                const ignore = payload.type == SocketPayloadType.Unknown;
                                if (!ignore &&
                                    ((hasConnectionId && (payload.payload.connectionId != this.connectionId)) ||
                                        (hasConnectionId && (payload.type & SocketPayloadType.DealView)) ||
                                        (hasConnectionId && (payload.type & SocketPayloadType.RealtimeDeal))
                                    )) {
                                    this.data.next(payload);
                                    // console.log('socket response', payload)
                                }
                                const isSentToFinance = payload?.payload?.deal?.status?.toLowerCase() === ViewConstants.DEAL_STATUS_SENT_TO_FINANCE.toLowerCase();
                                if(payload.type & SocketPayloadType.RealtimeDeal && isSentToFinance){
                                    //Although it's a DEAL_UPDATE event, but we cannto listen to as other DEAL_UPDATE events because
                                    //no "connectionId" is associated with it. So, we have to handle individually. 
                                    this.sendToFinanceEvent.next(payload.payload)
                                }
                                if (!hasConnectionId && !isNil(payload)
                                    && !isNil(payload.payload)
                                    && !isNil(payload.payload.deal)
                                    && !isNil(payload.payload.deal.status)
                                    && payload.payload.deal.status === "DEALER APPROVED"
                                    && payload.payload.type !== "OCA_SUBMITTED") {
                                    this.data.next(payload);
                                }
                                 if (!hasConnectionId && !isNil(payload)
                                    && !isNil(payload.payload)
                                    && !isNil(payload.payload.deal)
                                    && !isNil(payload.payload.deal.status)
                                     && payload.payload.type === "OCA_SUBMITTED")
                                     {
                                    this.data.next(payload);
                                }
                                // if (!ignore && ((hasConnectionId && (payload.payload.connectionId != this.connectionId)) || (hasConnectionId && (payload.type & SocketPayloadType.RealtimeDeal)))) {
                                //     this.data.next(payload);
                                // }
                            }
                        },
                        err => {
                            this.socketIsOpen = false;
                            this.checkError(false);
                            clearInterval(this.heartbeat_interval);
                            this.heartbeat_interval = null;
                            setTimeout(() => {
                                this.connect();
                            }, this.reconnectDelay);
                        },
                        () => {
                            //
                            // complete is called when server closes the connection
                            // or there is no more subsriber. (which is not gonna be the case here)
                            // reconnect immediately
                            //
                            clearInterval(this.heartbeat_interval);
                            this.heartbeat_interval = null;
                            this.socketIsOpen = false;
                            this.checkError(false);
                            this.connect();
                        });
                    this.socketIsOpen = true;
                    // 
                    // request the connectionId for this connection
                    //
                    this.send({ message: "connectionid", data: {deal: {dealTransactionId:this.dealTransactionId, salesPerson:this.salesPerson}}});
                    this.send({ message: "sendmessage", action: "sendmessage", data: {type:"DEAL_VIEW", deal: {dealTransactionId:this.dealTransactionId, salesPerson:this.salesPerson}}});

                }
            });
        }
    }
    //
    // check if websocket is active, otherwise open a new connection
    //
    private checkConnection() {
        if (!this.socketIsOpen && !this.sharedDataService.sessionExpiredAlert) {
            this.connect();
        }
    }

    // 
    // finds the type enum based on the string value in the payload
    //
    private getType(msg: SocketPayload) {
        let type = SocketPayloadType.Unknown;
        switch (msg.type) {
            case "DEAL_UPDATE":
                //
                // this type is a realtime by default
                //
                type = SocketPayloadType.RealtimeDeal;

                //
                // it can also be a notification if the following condition holds:
                //
                const onlineStatus=! !isNil(msg.deal.onlineStatus)?msg.deal.onlineStatus:"";
                const status = msg.deal.status.toUpperCase();
                const notificationStatus = ["PENDING REVIEW", "FINALIZED"];

                if (notificationStatus.includes(status) && onlineStatus!=="READY TO SHARE WITH DEALER") {
                    type |= SocketPayloadType.Notification;
                }
                break;
            case "DEAL_COMMENT":
                type = SocketPayloadType.Chat;
                break;
            case "DEAL_COMMENT_VISITED":
                type = SocketPayloadType.ChatVisisted;
                break;
            case "DEAL_VIEW":
                type = SocketPayloadType.DealView;
                break;
            case "DEAL_LEFT":
                type = SocketPayloadType.DealLeft;
                break;
            case "GET_ACTIVE_CONNECTIONS":
                type = SocketPayloadType.ActiveConnections;
                break;
            case "CONNECTIONID":
                this.missed_heartbeats = 0;
                type = SocketPayloadType.ConnectionId;
                break;
            case "PRODUCT_VISIBLE":
                type = SocketPayloadType.PrductVisible;
                break;
            case "PRODUCT_CHANGED":
                type = SocketPayloadType.PrductChanged;
                break;
            case "PRODUCT_SELECTED":
                type = SocketPayloadType.PrductSelected;
                break;
            case "USER_LOGOUT":
                type = SocketPayloadType.UserLogout;
                break;
            case "USER_LOGIN":
                type = SocketPayloadType.UserLogin;
                break;
            case "CUSTOMER_SWITCH":
                type = SocketPayloadType.CustomerSwitch;
                break;
            case "DASHBOARD":
                type = SocketPayloadType.Dashboard;
                break;
            case "DOFU_CHANGED":
                type = SocketPayloadType.dofuUpdated;
                break;
            case "DEAL_DESKING":
                type=SocketPayloadType.Desking;
                break;
            case "OCA_SUBMITTED":
                type=SocketPayloadType.CreditAppSubmitted;
                break;
            default:
                type = SocketPayloadType.Unknown;
        }
        return type;
    }

    //
    // tracks number of consecutive fails on connecting to server
    // if counts = 10, dispatches an event set the app status to offline.
    //
    private _errorCount = 0;
    private checkError(reset: boolean) {
        //
        // socket is connected. 
        //
        if (reset) {
            this._errorCount = 0;
            //
            // dispatch to set the app status to online
            //
            this._store.dispatch(appStatusActions.AppStatusSetOnline());
            return;
        }
        //
        //  
        // 
        if (this._errorCount < 11) {
            this._errorCount++;
        }

        //
        // dispatch an event to set the app status to offline
        //    
        if (this._errorCount == 10) {
            this._store.dispatch(appStatusActions.AppStatusSetOffline());
        }

    }
    // this method is to monitor the connection of socket by sending socket message every 20 seconds
    private heartbeatHandler() {
        if (this.heartbeat_interval === null || this.heartbeat_interval == undefined) {
            this.missed_heartbeats = 0;
            this.heartbeat_interval = setInterval(() => {
                this.missed_heartbeats++;
                if (this.missed_heartbeats >= 2) {  //if it misses two heartbeats then close throw error.
                    clearInterval(this.heartbeat_interval);
                    this.heartbeat_interval = null;
                    this.ws.error();
                } else {
                    this.send({ message: "connectionid", data: {deal: {dealTransactionId:this.dealTransactionId, salesPerson:this.salesPerson}}});
                }
            }, 20000);
        }
    }
}

//
// flag enum. use powers of 2 for any new state
//

export enum SocketPayloadType {

    Unknown = 0,    // 000000000000
    Chat = 1,    // 000000000001
    RealtimeDeal = 2,    // 000000000010
    DealView = 4,    // 000000000100
    Notification = 8,    // 000000001000
    ConnectionId = 16,   // 000000010000
    PrductSelected = 32,   // 000000100000
    PrductVisible = 64,   // 000001000000
    PrductChanged = 128,  // 000010000000
    UserLogout = 256,  // 000100000000
    UserLogin = 512,  // 001000000000
    CustomerSwitch = 1024, // 010000000000
    Dashboard = 2048,  // 100000000000
    dofuUpdated = 4096, //1000000000000
    DealLeft = 8192,    // 10000000000000
    ActiveConnections = 16384,    // 100000000000000
    ChatVisisted = 32768,
    Desking=65536,
    CreditAppSubmitted=131072
}
export interface SocketMessage {
    type: SocketPayloadType | number;
    payload: SocketPayload;
}